import React, { useCallback, useEffect, useState } from "react";
import {
  createEmployee,
  deleteEmployee,
  getEmployees,
  updateEmployee,
} from "../utils/api.js";
import { WEEKDAY_NAMES, DEFAULT_PAYDAY } from "../utils/payroll.js";
import { Button, Card, Input, Badge } from "../components/legacy-ui.js";
import { AppModalFooter, AppModalShell, AppPageHeader, AppTable } from "../components/app-ui.js";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { useAuth } from "../context/AuthContext.js";
import { Helmet } from "react-helmet";
import { companyPageTitle } from "../utils/company.js";
import { formatCurrency } from "../utils/formatters.js";
import { LoadingState } from "../components/motion.js";
import toast from "react-hot-toast";

const emptyForm = {
  name: "",
  documentId: "",
  phone: "",
  role: "Quesero",
  weeklySalary: "",
  payday: DEFAULT_PAYDAY,
  isActive: true,
  notes: "",
};

const EmployeesModule = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchEmployees = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await getEmployees({
        search: debouncedSearch || undefined,
      });
      setEmployees(res.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudieron cargar los empleados", {
        position: "top-center",
      });
    } finally {
      setLoading(false);
    }
  }, [user, debouncedSearch]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const resetForm = () => {
    setShowForm(false);
    setEditing(null);
    setFormData(emptyForm);
  };

  const handleAddNew = () => {
    setEditing(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const handleEdit = (employee) => {
    setEditing(employee);
    setFormData({
      name: employee.name || "",
      documentId: employee.documentId || "",
      phone: employee.phone || "",
      role: employee.role || "Quesero",
      weeklySalary:
        employee.weeklySalary != null ? String(employee.weeklySalary) : "",
      payday: employee.payday ?? DEFAULT_PAYDAY,
      isActive: employee.isActive !== false,
      notes: employee.notes || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este empleado y su historial de nómina?")) return;
    try {
      await deleteEmployee(id);
      toast.success("Empleado eliminado", { position: "top-center" });
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo eliminar", {
        position: "top-center",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("El nombre es obligatorio", { position: "top-center" });
      return;
    }
    const salary = Number(String(formData.weeklySalary).replace(",", "."));
    if (formData.weeklySalary !== "" && (!Number.isFinite(salary) || salary < 0)) {
      toast.error("Salario inválido", { position: "top-center" });
      return;
    }
    const payload = {
      name: formData.name.trim(),
      documentId: formData.documentId.trim(),
      phone: formData.phone.trim(),
      role: formData.role.trim() || "Quesero",
      weeklySalary: formData.weeklySalary === "" ? 0 : salary,
      payday: Number(formData.payday),
      isActive: Boolean(formData.isActive),
      notes: formData.notes.trim(),
    };
    setSaving(true);
    try {
      if (editing) {
        await updateEmployee(editing._id, payload);
        toast.success("Empleado actualizado", { position: "top-center" });
      } else {
        await createEmployee(payload);
        toast.success("Empleado creado", { position: "top-center" });
      }
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo guardar", {
        position: "top-center",
      });
    } finally {
      setSaving(false);
    }
  };

  const form = (
    <form id="employee-form" onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Input
        label="Nombre *"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        placeholder="Ej. José Pérez"
        required
      />
      <Input
        label="Cédula"
        value={formData.documentId}
        onChange={(e) => setFormData({ ...formData, documentId: e.target.value })}
      />
      <Input
        label="Teléfono"
        value={formData.phone}
        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
      />
      <Input
        label="Cargo"
        value={formData.role}
        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
        placeholder="Quesero"
      />
      <Input
        label="Salario semanal (USD)"
        type="text"
        inputMode="decimal"
        value={formData.weeklySalary}
        onChange={(e) => setFormData({ ...formData, weeklySalary: e.target.value })}
        placeholder="0.00"
      />
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1.5">Día de cobro</label>
        <select
          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 bg-white text-sm"
          value={formData.payday}
          onChange={(e) => setFormData({ ...formData, payday: Number(e.target.value) })}
        >
          {WEEKDAY_NAMES.map((label, index) => (
            <option key={label} value={index}>
              {label}
            </option>
          ))}
        </select>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-700 mt-6">
        <input
          type="checkbox"
          checked={formData.isActive}
          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
          className="rounded border-gray-300"
        />
        Activo
      </label>
      <div className="md:col-span-2">
        <Input
          label="Notas"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
        />
      </div>
    </form>
  );

  return (
    <>
      <Helmet>
        <title>{companyPageTitle("Empleados")}</title>
      </Helmet>
      <div className="app-page">
        <div className="app-section-head">
          <AppPageHeader
            title="Empleados (Queseros)"
            subtitle="Datos, salario semanal y día de cobro"
            actions={
              <Button onClick={handleAddNew}>
                <Plus size={16} /> Nuevo
              </Button>
            }
          />
          <div className="relative mt-4 mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Buscar por nombre, cédula o cargo…"
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm placeholder-gray-400"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="app-content">
          {loading ? (
            <LoadingState label="Cargando empleados" />
          ) : (
            <>
              <div className="md:hidden space-y-3">
                {employees.map((employee) => (
                  <Card key={employee._id} className="p-4">
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <h3 className="font-bold text-gray-900">{employee.name}</h3>
                        <p className="text-sm text-gray-500">{employee.role || "Quesero"}</p>
                        <p className="text-sm font-semibold text-gray-800 mt-1">
                          {formatCurrency(employee.weeklySalary || 0)} / sem.
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          Cobra los {WEEKDAY_NAMES[employee.payday ?? DEFAULT_PAYDAY]}
                        </p>
                      </div>
                      <Badge type={employee.isActive !== false ? "success" : "info"}>
                        {employee.isActive !== false ? "Activo" : "Inactivo"}
                      </Badge>
                    </div>
                    <div className="flex gap-2 justify-end mt-3">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(employee)}>
                        <Pencil size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(employee._id)}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </Card>
                ))}
                {employees.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-8">No hay empleados registrados</p>
                )}
              </div>

              <div className="hidden md:block">
                <AppTable
                  columns={[
                    { key: "name", title: "Nombre" },
                    { key: "role", title: "Cargo" },
                    { key: "salary", title: "Salario", align: "right" },
                    { key: "payday", title: "Día de cobro" },
                    { key: "status", title: "Estado", align: "center" },
                    { key: "actions", title: "Acciones", align: "center" },
                  ]}
                >
                  {employees.map((employee) => (
                    <tr key={employee._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3.5 text-sm font-medium text-gray-900">
                        {employee.name}
                        {employee.documentId ? (
                          <span className="block text-xs text-gray-400">{employee.documentId}</span>
                        ) : null}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-gray-600">{employee.role || "Quesero"}</td>
                      <td className="px-6 py-3.5 text-sm text-right font-medium">
                        {formatCurrency(employee.weeklySalary || 0)}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-gray-600">
                        {WEEKDAY_NAMES[employee.payday ?? DEFAULT_PAYDAY]}
                      </td>
                      <td className="px-6 py-3.5 text-center">
                        <Badge type={employee.isActive !== false ? "success" : "info"}>
                          {employee.isActive !== false ? "Activo" : "Inactivo"}
                        </Badge>
                      </td>
                      <td className="px-6 py-3.5 text-center">
                        <div className="flex gap-1 justify-center">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(employee)}>
                            <Pencil size={16} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(employee._id)}>
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </AppTable>
                {employees.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-8">No hay empleados registrados</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <AppModalShell
        open={showForm}
        onClose={resetForm}
        title={editing ? "Editar empleado" : "Nuevo empleado"}
        widthClass="max-w-lg"
        fitContent
        footer={
          <AppModalFooter>
            <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={resetForm}>
              Cancelar
            </Button>
            <Button type="submit" form="employee-form" className="w-full sm:w-auto" disabled={saving}>
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </AppModalFooter>
        }
      >
        {form}
      </AppModalShell>
    </>
  );
};

export default EmployeesModule;
